import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ZFlixSplashLockup from './ZFlixSplashLockup';
import ZFlixSplashLogo from './ZFlixSplashLogo';
import { getSplashBloxTiming, SPLASH_BOING_IMPACT_TIMES_S } from './splashConstants';
import {
  armSplashTimelineAudio,
  flushSplashAudioLog,
  isSplashAudioUnlockedRecently,
  isSplashBoingWebAudioScheduled,
  isSplashWebAudioHealthy,
  playBoing,
  prepareSplashAudio,
  playSplashSting,
  resetSplashBoingScheduleState,
  unlockSplashAudioFromGesture,
} from './splashSound';
import {
  armSplashTimeline,
  getSplashElapsedMs,
  isSplashBoingWebAudioActive,
  isSplashTimelineArmed,
  resetSplashTimeline,
  tryAutoArmSplashTimelineAfterLogin,
} from './splashTimeline';

const ICON_W = 220;
const ICON_H = ICON_W;
const LOGO_PX = 340;

const PAD = 14;
const SQUASH_HOLD_S = 0.08;
const SQUASH_DECAY = 10;
const FLOOR_PRESS_PX = 4;

/** Deterministic impact schedule (seconds). squash: [scaleX, scaleY]. */
const IMPACTS = [
  { t: SPLASH_BOING_IMPACT_TIMES_S[0], x: 0.22, surface: 'floor', squash: [1.22, 0.68] },
  { t: SPLASH_BOING_IMPACT_TIMES_S[1], x: 0.78, surface: 'ceiling', squash: [1.18, 0.72] },
  { t: SPLASH_BOING_IMPACT_TIMES_S[2], x: 'center', surface: 'floor', squash: [1.14, 0.76] },
];

const T_LOCKUP = IMPACTS[IMPACTS.length - 1].t;

function quadBezier(p0, p1, p2, u) {
  const o = 1 - u;
  return {
    x: o * o * p0.x + 2 * o * u * p1.x + u * u * p2.x,
    y: o * o * p0.y + 2 * o * u * p1.y + u * u * p2.y,
  };
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * @param {number} t elapsed seconds
 * @param {{
 *   pad: number; maxX: number; maxY: number; cx: number; cy: number; dropY: number;
 *   xFloor1: number; xCeiling: number;
 * }} b
 */
function samplePosition(t, b) {
  const { pad, maxY, maxX, cx, cy, dropY, xFloor1, xCeiling } = b;
  const t1 = IMPACTS[0].t;
  const t2 = IMPACTS[1].t;
  const t3 = IMPACTS[2].t;

  let x;
  let y;
  let rot;

  if (t < t1) {
    const u = t / t1;
    const ease = u * u;
    x = xFloor1;
    y = dropY + (maxY - dropY) * ease;
    rot = -5 + 10 * u;
  } else if (t < t2) {
    const u = (t - t1) / (t2 - t1);
    const midX = (xFloor1 + xCeiling) * 0.5;
    const p = quadBezier(
      { x: xFloor1, y: maxY },
      { x: midX, y: pad },
      { x: xCeiling, y: pad },
      u,
    );
    x = p.x;
    y = p.y;
    rot = 5 - 12 * u;
  } else if (t < t3) {
    const u = (t - t2) / (t3 - t2);
    const p = quadBezier(
      { x: xCeiling, y: pad },
      { x: cx, y: (pad + cy) * 0.5 },
      { x: cx, y: cy },
      u,
    );
    x = p.x;
    y = p.y;
    rot = -7 + 7 * u;
  } else {
    x = cx;
    y = cy;
    rot = 0;
  }

  return {
    x: clamp(x, pad, maxX),
    y: t < t1 ? y : clamp(y, pad, maxY),
    rot,
  };
}

/**
 * Blox splash: timed drop → 3 wall impacts → center lockup.
 * @param {{ reducedMotion?: boolean; playSound?: boolean; onSettled?: () => void }} props
 */
export default function SplashBloxBounce({ reducedMotion = false, playSound = true, onSettled }) {
  const { lockupHoldMs, lockupTaglineDelayMs, lockupTaglineFadeMs, wordmarkFadeMs } = useMemo(
    () => getSplashBloxTiming(),
    [],
  );
  const stageRef = useRef(null);
  const bloxRef = useRef(null);
  const markRef = useRef(null);
  const rafRef = useRef(0);
  const lastFrameRef = useRef(0);
  const firedImpactsRef = useRef([]);
  const playedBoingRef = useRef([]);
  const settledRef = useRef(false);
  const stingPlayedRef = useRef(false);
  const lockupTimerRef = useRef(0);
  const lockupStartedRef = useRef(false);
  const webAudioActiveRef = useRef(false);
  const squashRef = useRef({ x: 1, y: 1, press: 0, holdUntil: 0 });
  const [lockupVisible, setLockupVisible] = useState(reducedMotion);
  const lockupCopyStyle = useMemo(
    () => ({
      '--zflix-blox-tagline-delay': `${lockupTaglineDelayMs}ms`,
      '--zflix-blox-tagline-fade-ms': `${lockupTaglineFadeMs}ms`,
      '--zflix-blox-wordmark-fade-ms': `${wordmarkFadeMs}ms`,
    }),
    [lockupTaglineDelayMs, lockupTaglineFadeMs, wordmarkFadeMs],
  );

  const beginLockup = useCallback(() => {
    if (lockupStartedRef.current) return;
    lockupStartedRef.current = true;
    setLockupVisible(true);
    window.setTimeout(flushSplashAudioLog, 1200);
    if (playSound && !stingPlayedRef.current) {
      stingPlayedRef.current = true;
      playSplashSting();
    }
    lockupTimerRef.current = window.setTimeout(() => {
      if (!settledRef.current) {
        settledRef.current = true;
        onSettled?.();
      }
    }, lockupHoldMs);
  }, [lockupHoldMs, onSettled, playSound]);

  useEffect(() => () => {
    window.clearTimeout(lockupTimerRef.current);
    resetSplashTimeline();
  }, []);

  useEffect(() => {
    if (!playSound || reducedMotion) return undefined;

    if (tryAutoArmSplashTimelineAfterLogin()) {
      webAudioActiveRef.current = isSplashBoingWebAudioActive();
      return undefined;
    }

    const opts = { capture: true, passive: true };
    let removed = false;

    const removeGestureListeners = () => {
      if (removed) return;
      removed = true;
      document.removeEventListener('pointerdown', onGesture, opts);
      document.removeEventListener('keydown', onGesture, opts);
    };

    const onGesture = (ev) => {
      /** Boings already on the audio clock (TV app): a remote press must not schedule them a second time. */
      if (isSplashBoingWebAudioActive()) {
        removeGestureListeners();
        return;
      }
      unlockSplashAudioFromGesture(ev);
      resetSplashBoingScheduleState();
      const elapsedS = getSplashElapsedMs() / 1000;
      armSplashTimelineAudio(SPLASH_BOING_IMPACT_TIMES_S, elapsedS);
      webAudioActiveRef.current = isSplashBoingWebAudioActive();
      removeGestureListeners();
    };

    document.addEventListener('pointerdown', onGesture, opts);
    document.addEventListener('keydown', onGesture, opts);
    return () => {
      removeGestureListeners();
    };
  }, [playSound, reducedMotion]);

  useEffect(() => {
    if (reducedMotion) {
      settledRef.current = true;
      onSettled?.();
      return undefined;
    }

    const measure = () => {
      const stage = stageRef.current;
      if (!stage) return null;
      const rect = stage.getBoundingClientRect();
      const maxX = Math.max(PAD, rect.width - ICON_W - PAD);
      const maxY = Math.max(PAD, rect.height - ICON_H - PAD);
      const cx = (rect.width - ICON_W) / 2;
      const cy = (rect.height - ICON_H) / 2;
      const fracX = (frac) => frac * rect.width - ICON_W / 2;
      const dropY = -(ICON_H + rect.height * 0.5);
      return {
        pad: PAD,
        maxX,
        maxY,
        cx,
        cy,
        dropY,
        xFloor1: fracX(IMPACTS[0].x),
        xCeiling: fracX(IMPACTS[1].x),
      };
    };

    const applyOuter = (el, x, y, rot) => {
      el.style.opacity = '1';
      el.style.visibility = 'visible';
      el.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${rot}deg)`;
    };

    const applyMark = (markEl, sx, sy, press) => {
      if (!markEl) return;
      markEl.style.transform = `translate3d(0, ${press}px, 0) scale(${sx}, ${sy})`;
    };

    const applySquash = (impact, impactIndex, tNow) => {
      const [sx, sy] = impact.squash;
      const press =
        impact.surface === 'floor' && impactIndex === 0 ? FLOOR_PRESS_PX : 0;
      squashRef.current = {
        x: sx,
        y: sy,
        press,
        holdUntil: tNow + SQUASH_HOLD_S,
      };
    };

    const decaySquash = (sq, dt, tSec) => {
      if (tSec < sq.holdUntil) return;
      const k = Math.min(1, SQUASH_DECAY * dt);
      sq.x += (1 - sq.x) * k;
      sq.y += (1 - sq.y) * k;
      sq.press += (0 - sq.press) * k;
    };

    const checkImpacts = (t) => {
      IMPACTS.forEach((impact, i) => {
        if (t < impact.t || firedImpactsRef.current[i]) return;
        firedImpactsRef.current[i] = true;
        applySquash(impact, i, t);
        if (playSound && !playedBoingRef.current[i]) {
          playedBoingRef.current[i] = true;
          if (!webAudioActiveRef.current && !isSplashBoingWebAudioScheduled(i)) {
            playBoing();
          } else if (!isSplashWebAudioHealthy()) {
            /** Scheduled on Web Audio, but its clock is not moving on this device — it would stay silent. */
            playBoing({ preferHtml: true });
          }
        }
      });
    };

    const paintFrame = (t) => {
      const icon = bloxRef.current;
      const mark = markRef.current;
      if (!icon) return;

      const b = measure();
      if (!b) return;

      checkImpacts(t);
      const sq = squashRef.current;
      decaySquash(sq, 0, t);

      if (t >= T_LOCKUP) {
        applyOuter(icon, b.cx, b.cy, 0);
        applyMark(mark, sq.x, sq.y, sq.press);
        if (!icon.classList.contains('zflix-splash-blox-icon--lockup')) {
          icon.classList.add('zflix-splash-blox-icon--lockup');
        }
        beginLockup();
        return;
      }

      const { x, y, rot } = samplePosition(t, b);
      applyOuter(icon, x, y, rot);
      applyMark(mark, sq.x, sq.y, sq.press);
    };

    let cancelled = false;

    const tick = (now) => {
      if (cancelled) return;
      const icon = bloxRef.current;
      const mark = markRef.current;
      if (!icon) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const tFrame = now ?? performance.now();

      if (!isSplashTimelineArmed()) {
        paintFrame(0);
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const dt = Math.min(0.05, (tFrame - lastFrameRef.current) / 1000);
      lastFrameRef.current = tFrame;
      const t = getSplashElapsedMs() / 1000;

      const b = measure();
      if (!b) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      checkImpacts(t);
      const sq = squashRef.current;
      decaySquash(sq, dt, t);

      if (t >= T_LOCKUP) {
        applyOuter(icon, b.cx, b.cy, 0);
        applyMark(mark, sq.x, sq.y, sq.press);
        if (!icon.classList.contains('zflix-splash-blox-icon--lockup')) {
          icon.classList.add('zflix-splash-blox-icon--lockup');
        }
        beginLockup();
        if (sq.x > 1.001 || sq.y < 0.999 || Math.abs(sq.press) > 0.1) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        return;
      }

      const { x, y, rot } = samplePosition(t, b);
      applyOuter(icon, x, y, rot);
      applyMark(mark, sq.x, sq.y, sq.press);
      rafRef.current = requestAnimationFrame(tick);
    };

    firedImpactsRef.current = [];
    playedBoingRef.current = [];
    squashRef.current = { x: 1, y: 1, press: 0, holdUntil: 0 };
    lockupStartedRef.current = false;

    const startTimeline = (audioReady) => {
      if (cancelled) return;
      armSplashTimeline();
      if (playSound && !tryAutoArmSplashTimelineAfterLogin() && audioReady) {
        /** No login tap (TV app, page refresh): put all three boings on the audio clock now, in step with the drop. */
        resetSplashBoingScheduleState();
        armSplashTimelineAudio(SPLASH_BOING_IMPACT_TIMES_S, getSplashElapsedMs() / 1000);
      }
      lastFrameRef.current = performance.now();
      webAudioActiveRef.current = isSplashBoingWebAudioActive();
    };

    if (playSound && !isSplashTimelineArmed() && !isSplashAudioUnlockedRecently(10000)) {
      /** Hold the logo off-screen until audio can be scheduled, so the first bounce is not waiting on a decode. */
      prepareSplashAudio().then(startTimeline, () => startTimeline(false));
    } else {
      startTimeline(false);
    }
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
    };
  }, [reducedMotion, playSound, beginLockup, onSettled]);

  if (reducedMotion) {
    return (
      <div className="zflix-splash-stage zflix-splash-blox zflix-splash-stage--static">
        <ZFlixSplashLockup
          size="large"
          animateIn={false}
          iconPopIn={false}
          className="zflix-splash-lockup--blox-glow"
          showWordmark
          showTagline
        />
      </div>
    );
  }

  return (
    <div ref={stageRef} className="zflix-splash-stage zflix-splash-blox">
      <BloxIcon bloxRef={bloxRef} markRef={markRef} lockup={lockupVisible} />
      {lockupVisible && (
        <div className="zflix-splash-blox-lockup-layer" aria-hidden>
          <div className="zflix-splash-blox-lockup-glow" aria-hidden />
          <div className="zflix-splash-blox-lockup-copy" style={lockupCopyStyle}>
            <ZFlixSplashLockup
              size="large"
              hideIcon
              animateIn
              iconPopIn={false}
              showWordmark
              showTagline
            />
          </div>
        </div>
      )}
    </div>
  );
}

function BloxIcon({ bloxRef, markRef, lockup }) {
  return (
    <div
      ref={bloxRef}
      className={`zflix-splash-blox-icon${lockup ? ' zflix-splash-blox-icon--lockup' : ''}`}
      style={{ width: ICON_W, height: ICON_H, opacity: 1 }}
    >
      <div className="zflix-splash-blox-glow" aria-hidden />
      <div ref={markRef} className="zflix-splash-blox-mark">
        <ZFlixSplashLogo iconOnly iconPx={LOGO_PX} />
      </div>
    </div>
  );
}
